import React, { useEffect, useState } from "react"
import { Plan } from "../../types/subscription"
import { SubscriptionService } from "../../service/SubscriptionService"
import { Badge } from "primereact/badge"
import { Button } from "primereact/button"
import moment from "moment"
import { Dropdown } from "primereact/dropdown"
import {
  SubscriptionStatus,
  getPriceIds,
  UNLIMITED,
  PAY_AS_YOU_GO,
  statusBadgeText,
  statusBadgeStyle,
  planTypes
} from "./utils"
import { OrdersSyncService } from "../../service/OrdersSyncService"
import { Message } from "primereact/message"
import Stripe from "stripe"

const ORDERS_TRIAL_LIMIT = 500

// Is subscription scheduled, hide the Switch Plan button
// Display a message that the subscription will be updated on the next billing date
// Add a button to cancel the subscription plan change schedule

const SubscriptionPage = () => {
  const [subscriptionDetails, setSubscriptionDetails] = useState<Stripe.Subscription>()
  const [stripePortalUrl, setStripePortalUrl] = useState<string>()
  const [stripeCheckoutUrl, setStripeCheckoutUrl] = useState<string>()
  const [planType, setPlanType] = useState<Plan>(Plan.PAY_AS_YOU_GO)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingCheckout, setIsLoadingCheckout] = useState(false)
  const [syncedOrdersCount, setSyncedOrdersCount] = useState(0)

  useEffect(() => {
    ;(async () => {
      try {
        const countSyncedOrdersResponse = await OrdersSyncService.countCurrentSubscriptionSyncedOrders()

        setSyncedOrdersCount(countSyncedOrdersResponse?.syncedOrders)

        const data = await SubscriptionService.getDetails()

        if (data?.subscription && data?.portal_url) {
          setSubscriptionDetails(data.subscription)
          setStripePortalUrl(data.portal_url)
        } else {
          const checkoutData = await SubscriptionService.activate({ plan_type: Plan.PAY_AS_YOU_GO })

          setStripeCheckoutUrl(checkoutData.url)
        }
      } catch (error) {
        console.error(error)
      } finally {
        setIsLoading(false)
      }
    })()
  }, [])

  // Get price IDs based on current environment
  const priceIds = getPriceIds(process.env.REACT_APP_ENVIRONMENT || "dev")
  const planId = subscriptionDetails?.items.data[0].plan.id || ""

  const isTrialing = planId === priceIds[Plan.PAY_AS_YOU_GO] && syncedOrdersCount < ORDERS_TRIAL_LIMIT

  const status = subscriptionDetails?.status as SubscriptionStatus
  const subscriptoinId = subscriptionDetails?.id
  const subscriptionSchedule = subscriptionDetails?.schedule as Stripe.SubscriptionSchedule
  const subscriptionScheduleId = subscriptionSchedule?.id
  const currentPlanId = planId
  const currentPlanName = currentPlanId === priceIds[Plan.UNLIMITED] ? UNLIMITED : PAY_AS_YOU_GO
  const updateButtonText =
    currentPlanId === priceIds[Plan.UNLIMITED] ? `Switch to ${PAY_AS_YOU_GO}` : `Upgrade to ${UNLIMITED}`
  const updatePlanToValue = currentPlanId === priceIds[Plan.UNLIMITED] ? Plan.PAY_AS_YOU_GO : Plan.UNLIMITED

  const subscriptionStatus = subscriptionDetails ? (
    <Badge value={statusBadgeText[status]} severity={statusBadgeStyle[status]}></Badge>
  ) : null

  const handleManageSubscription = async () => {
    if (stripePortalUrl) {
      window.location.href = stripePortalUrl
    }
  }

  const handleSelectChange = async (value: Plan) => {
    setIsLoadingCheckout(true)
    const checkoutData = await SubscriptionService.activate({ plan_type: value })
    setStripeCheckoutUrl(checkoutData.url)
    setPlanType(value)
    setIsLoadingCheckout(false)
  }

  const handleActivateSubscription = async () => {
    if (stripeCheckoutUrl) {
      window.location.href = stripeCheckoutUrl
    }
  }

  const handleUpdateSubscription = async (planType: Plan) => {
    if (subscriptoinId) {
      try {
        setIsLoading(true)

        const response = await SubscriptionService.update({
          stripe_subscription_id: subscriptoinId,
          new_subscription_type: planType
        })

        setSubscriptionDetails(response.subscription)
        setIsLoading(false)
      } catch (error) {
        console.error("Error updating subscription.", error)
      }
    }
  }

  const handleReleaseSubscriptionSchedule = async (subscriptionScheduleId: string) => {
    if (subscriptionScheduleId) {
      try {
        setIsLoading(true)
        const response = await SubscriptionService.releaseSchedule({
          subscription_schedule_id: subscriptionScheduleId
        })
        console.log("response", response)
        setSubscriptionDetails(response.subscription)
        setIsLoading(false)
      } catch (error) {
        console.error("Error canceling subscription schedule.", error)
      }
    }
  }

  const isCancelled = subscriptionDetails?.cancel_at_period_end
  const cancelAt =
    subscriptionDetails?.cancel_at && moment(subscriptionDetails.cancel_at * 1000).format("MMMM Do YYYY")

  const schedule = subscriptionDetails?.schedule as Stripe.SubscriptionSchedule
  const nextBillingDate = schedule?.phases[1].start_date

  return (
    <div className="card">
      <div className="mb-4">
        <h2 className="flex items-start gap-2">Subscription {!isLoading && subscriptionStatus}</h2>
        <div className="flex flex-column items-start gap-2">
          {isLoading ? (
            <div>
              <p>Loading...</p>
            </div>
          ) : subscriptionDetails ? (
            <>
              {isCancelled && (
                <Message
                  severity="warn"
                  text={`Your Stripe Subscription will remain active until ${cancelAt}`}
                />
              )}
              <div></div>
              <div>
                <p>
                  Current Plan: <strong>{currentPlanName}</strong>.
                </p>
                {schedule && (
                  <p>
                    Your subscription will be changed to <b>Pay-As-You-Go</b> on{" "}
                    {moment(nextBillingDate * 1000).format("MMMM Do YYYY")}.{" "}
                    <Button
                      label="Cancel"
                      className="button-as-link"
                      onClick={() => handleReleaseSubscriptionSchedule(subscriptionScheduleId)}
                    />
                  </p>
                )}
                <p>Total synced orders count: {syncedOrdersCount}</p>
                {isTrialing && (
                  <p>{`The trial limit for this plan is up to ${ORDERS_TRIAL_LIMIT} synced orders.`}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  label="Go to Stripe"
                  className="p-button p-button-primary"
                  onClick={() => handleManageSubscription()}
                />
                {!isCancelled && !schedule && (
                  <Button
                    label={updateButtonText}
                    className="p-button p-button-primary"
                    onClick={() => handleUpdateSubscription(updatePlanToValue)}
                  />
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-column gap-2 w-4">
              <div>
                <p>To activate you subscription, choose a plan type and start the free trial.</p>
              </div>
              <Dropdown value={planType} onChange={(e) => handleSelectChange(e.value)} options={planTypes} />
              <div>
                <Button
                  label="Activate"
                  className="p-button p-button-primary"
                  onClick={() => handleActivateSubscription()}
                  loading={isLoadingCheckout}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default SubscriptionPage
